/**
 * FPSDisplay - FPS 显示组件
 * @description 开发模式下显示实时帧率，帮助性能监控
 */

import Phaser from 'phaser';

/** FPS 显示配置 */
export interface FPSDisplayConfig {
  /** 显示位置 x，默认右上角 */
  x?: number;
  /** 显示位置 y，默认右上角 */
  y?: number;
  /** 是否显示详细物理信息 */
  showPhysicsInfo?: boolean;
}

const DEFAULT_CONFIG: Required<FPSDisplayConfig> = {
  x: 750,
  y: 10,
  showPhysicsInfo: false,
};

/**
 * FPSDisplay 类
 * 实时显示游戏帧率，开发模式下使用
 */
export class FPSDisplay {
  private scene: Phaser.Scene;
  private text: Phaser.GameObjects.Text;
  private config: Required<FPSDisplayConfig>;
  private physicsBodyCount: number = 0;

  /**
   * 创建 FPS 显示组件
   * @param scene Phaser 场景
   * @param config 配置选项
   */
  constructor(scene: Phaser.Scene, config?: FPSDisplayConfig) {
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...config };

    this.text = scene.add.text(this.config.x, this.config.y, '', {
      fontSize: '14px',
      color: '#ffff00',
      fontFamily: 'Arial',
      backgroundColor: '#00000080',
      padding: { x: 5, y: 3 },
    });
    this.text.setDepth(1000);
    this.text.setScrollFactor(0);
    scene.events.on('update', this.update, this);
  }

  /**
   * 更新 FPS 显示（每帧调用）
   */
  private update(): void {
    const game = this.scene.game;
    const fps = Math.round(game.loop.actualFps);

    const fpsColor = fps >= 55 ? '#4ade80' : fps >= 30 ? '#ffff00' : '#ff6b6b';

    let displayText = `FPS: ${fps}`;

    if (this.config.showPhysicsInfo) {
      displayText += ` | Bodies: ${this.physicsBodyCount}`;
    }

    this.text.setText(displayText);
    this.text.setColor(fpsColor);
  }

  /**
   * 设置物理对象数量（用于监控）
   * @param count 物理对象数量
   */
  setPhysicsBodyCount(count: number): void {
    this.physicsBodyCount = count;
  }

  /**
   * 显示物理信息
   */
  enablePhysicsInfo(): void {
    this.config.showPhysicsInfo = true;
  }

  /**
   * 隐藏物理信息
   */
  disablePhysicsInfo(): void {
    this.config.showPhysicsInfo = false;
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    this.scene.events.off('update', this.update, this);
    this.text.destroy();
  }
}